import type {ComponentProps} from 'react';
import Link from 'next/link';
import type {BreadcrumbList, WithContext} from 'schema-dts';

import {JsonLd} from '@/modules/foundation/components/json-ld';
import {siteUrl} from '@/shared/config';

type BreadcrumbItem = {
	title: string;
	href: string;
	active?: boolean;
};

type Props = {
	breadcrumb: BreadcrumbItem[];
} & Partial<ComponentProps<'nav'>>;

const generateBreadcrumbData = (breadcrumbs: BreadcrumbItem[]): WithContext<BreadcrumbList> => ({
	'@context': 'https://schema.org',
	'@type': 'BreadcrumbList',
	itemListElement: breadcrumbs?.map((breadcrumb, idx) => ({
		'@type': 'ListItem',
		position: idx + 1,
		name: breadcrumb.title,
		item: `${siteUrl}${breadcrumb.href}`,
	})),
});

export const Breadcrumb = ({breadcrumb, ...rest}: Props) => {
	const jsonLd = generateBreadcrumbData(breadcrumb);

	return (
		<>
			<JsonLd jsonLd={jsonLd} />
			<nav aria-label="Du er her" {...rest}>
				<ol className="flex flex-wrap gap-2 text-base text-neutral-100 uppercase">
					{breadcrumb.map((path) => {
						return (
							<li key={path.href} className="flex items-center gap-3">
								{path.active ? (
									<span
										aria-current="page"
										className="text-primary font-semibold"
									>
										{path.title}
									</span>
								) : (
									<>
										<Link
											href={path.href}
											className="rounded-sm hover:underline"
										>
											{path.title}
										</Link>
										<span aria-hidden className="mr-1 inline-block">
											/
										</span>
									</>
								)}
							</li>
						);
					})}
				</ol>
			</nav>
		</>
	);
};
